'use client';

import React from 'react';
import Link from 'next/link';
import { Globe, Shield } from 'lucide-react';

type Props = {
  handle: string;
  children: React.ReactNode;
};

type State = {
  crashed: boolean;
  message: string | null;
};

type SafeProfile = {
  id?: string;
  full_name?: string | null;
  public_handle?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  banner_url?: string | null;
  vault_id?: string | null;
};

type SafePayload = {
  profile?: SafeProfile;
  stats?: {
    publicStories?: number;
    publicPulsePosts?: number;
    publicCircles?: number;
    publicRecords?: number;
  };
};

function SafePublicProfileShell({ handle }: { handle: string }) {
  const [payload, setPayload] = React.useState<SafePayload | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/public/profile?handle=${encodeURIComponent(handle)}`, { cache: 'no-store' })
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled) setPayload(json);
      })
      .catch(() => {
        if (!cancelled) setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const profile = payload?.profile;
  const displayName = profile?.full_name || profile?.public_handle || handle;
  const stats = payload?.stats || {};

  return (
    <main
      style={{
        minHeight: '100vh',
        padding: '28px 16px',
        background: 'linear-gradient(165deg, #f8fafc 0%, #eef6ff 42%, #f0fdf9 100%)',
      }}
    >
      <section
        style={{
          maxWidth: 880,
          margin: '0 auto',
          overflow: 'hidden',
          borderRadius: 22,
          background: '#ffffff',
          border: '1px solid rgba(15,23,42,0.08)',
          boxShadow: '0 16px 44px rgba(15,23,42,0.08)',
        }}
      >
        <div
          style={{
            minHeight: 180,
            background: profile?.banner_url
              ? `linear-gradient(180deg,rgba(15,23,42,0.18),rgba(15,23,42,0.38)),url(${profile.banner_url}) center/cover`
              : 'linear-gradient(135deg,#0f172a,#1b6b5c)',
          }}
        />
        <div style={{ padding: '0 24px 26px' }}>
          <div
            style={{
              width: 92,
              height: 92,
              marginTop: -46,
              borderRadius: 24,
              border: '4px solid #fff',
              background: '#eef6f4',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1b6b5c',
              boxShadow: '0 10px 26px rgba(15,23,42,0.14)',
            }}
          >
            {profile?.avatar_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={profile.avatar_url}
                alt={displayName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Globe size={34} />
            )}
          </div>
          <div style={{ marginTop: 14 }}>
            <p
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#1b6b5c',
              }}
            >
              Public profile
            </p>
            <h1 style={{ margin: '6px 0 0', fontSize: 30, lineHeight: 1.1, color: '#0f1923' }}>
              {loading ? 'Loading profile...' : displayName}
            </h1>
            <p style={{ margin: '6px 0 0', color: '#64748b', fontWeight: 700 }}>
              @{profile?.public_handle || handle}
            </p>
            {profile?.bio ? (
              <p style={{ margin: '14px 0 0', maxWidth: 640, color: '#334155', lineHeight: 1.65 }}>
                {profile.bio}
              </p>
            ) : null}
          </div>
          <div
            style={{
              marginTop: 22,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
              gap: 10,
            }}
          >
            {[
              ['Stories', stats.publicStories ?? 0],
              ['Pulse', stats.publicPulsePosts ?? 0],
              ['Circles', stats.publicCircles ?? 0],
              ['Records', stats.publicRecords ?? 0],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  borderRadius: 14,
                  padding: 14,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <p style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#0f1923' }}>
                  {value}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                  {label}
                </p>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <Link
              href="/public-timeline"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                color: '#0f1923',
                background: '#fff',
                fontWeight: 800,
                textDecoration: 'none',
              }}
            >
              <Shield size={14} />
              Explore public records
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default class PublicProfileRuntimeBoundary extends React.Component<Props, State> {
  state: State = { crashed: false, message: null };

  static getDerivedStateFromError(error: Error): State {
    return { crashed: true, message: error.message };
  }

  componentDidCatch(error: Error) {
    console.error('[PublicProfileRuntimeBoundary] client-render-failed', {
      handle: this.props.handle,
      message: error.message,
      note: 'PublicProfileExperience crashed during render. Check section-render-failed logs for isolated section failures.',
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.handle !== this.props.handle && this.state.crashed) {
      this.setState({ crashed: false, message: null });
    }
  }

  render() {
    if (this.state.crashed) return <SafePublicProfileShell handle={this.props.handle} />;
    return this.props.children;
  }
}
